import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../lib/api-client';
import type { BreakApproach, BreakState } from '@chore-project/shared';

interface SetBreakDto {
  returnDate: string;
  approach: BreakApproach;
}

/** Invalidate everything a break touches: the profile (break state) + task/points views. */
function useInvalidateBreak() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ['users', 'me', 'profile'] });
    void queryClient.invalidateQueries({ queryKey: ['my-assignments'] });
    void queryClient.invalidateQueries({ queryKey: ['chore-assignments'] });
    void queryClient.invalidateQueries({ queryKey: ['points'] });
  };
}

/** Start (or update) the current user's break. */
export function useSetBreak() {
  const invalidate = useInvalidateBreak();
  return useMutation<BreakState, Error, SetBreakDto>({
    mutationFn: async (dto) => {
      const { data } = await apiClient.patch<BreakState>('/users/me/break', dto);
      return data;
    },
    onSuccess: invalidate,
  });
}

/** End the current user's break. */
export function useEndBreak() {
  const invalidate = useInvalidateBreak();
  return useMutation<void, Error, void>({
    mutationFn: async () => {
      await apiClient.delete('/users/me/break');
    },
    onSuccess: invalidate,
  });
}
